import { useState, useEffect, useCallback } from "react";
import classes from "./Management.module.scss";
import {
  getAnalyticsDashboard,
  getMostActiveUsers,
  getPopularCars,
  getPopularSearches,
  getPopularListings,
  getRecentActivity,
} from "../../services";

const PERIOD_OPTIONS = [
  { label: "7 Days", value: 7 },
  { label: "14 Days", value: 14 },
  { label: "30 Days", value: 30 },
  { label: "90 Days", value: 90 },
];

const EVENT_LABELS = {
  car_search: "Searches",
  car_view: "Car Views",
  car_favorite: "Favorites",
  bid_placed: "Bids Placed",
  inspection_requested: "Inspections",
  purchase_created: "Purchases",
  login: "Logins",
  register: "Registrations",
  page_view: "Page Views",
  dealer_favorite: "Dealer Stock Saves",
  dealer_inquiry: "Dealer Stock Inquiries",
  dealer_purchase_created: "Dealer Stock Purchases",
};

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState(30);
  const [activeSection, setActiveSection] = useState("overview");
  const [loading, setLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);
  const [popularCars, setPopularCars] = useState([]);
  const [popularSearches, setPopularSearches] = useState(null);
  const [popularListings, setPopularListings] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAnalyticsDashboard(period);
      if (res?.data) setDashboard(res.data);
    } catch (err) {
      console.error("Failed to load dashboard:", err);
    }
    setLoading(false);
  }, [period]);

  const fetchActiveUsers = useCallback(async () => {
    try {
      const res = await getMostActiveUsers(period, 20);
      if (res?.data) setActiveUsers(res.data);
    } catch (err) {
      console.error("Failed to load active users:", err);
    }
  }, [period]);

  const fetchPopularCars = useCallback(async () => {
    try {
      const res = await getPopularCars(period, 20);
      if (res?.data) setPopularCars(res.data);
    } catch (err) {
      console.error("Failed to load popular cars:", err);
    }
  }, [period]);

  const fetchPopularSearches = useCallback(async () => {
    try {
      const res = await getPopularSearches(period, 20);
      if (res?.data) setPopularSearches(res.data);
    } catch (err) {
      console.error("Failed to load popular searches:", err);
    }
  }, [period]);

  const fetchPopularListings = useCallback(async () => {
    try {
      const res = await getPopularListings(period, 20);
      if (res?.data) setPopularListings(res.data);
    } catch (err) {
      console.error("Failed to load popular listings:", err);
    }
  }, [period]);

  const fetchRecentActivity = useCallback(async () => {
    try {
      const res = await getRecentActivity(50);
      if (res?.data) setRecentActivity(res.data);
    } catch (err) {
      console.error("Failed to load recent activity:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (activeSection === "users") fetchActiveUsers();
    if (activeSection === "cars") fetchPopularCars();
    if (activeSection === "searches") fetchPopularSearches();
    if (activeSection === "listings") fetchPopularListings();
    if (activeSection === "activity") fetchRecentActivity();
  }, [
    activeSection,
    fetchActiveUsers,
    fetchPopularCars,
    fetchPopularSearches,
    fetchPopularListings,
    fetchRecentActivity,
  ]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const sections = [
    { id: "overview", label: "Overview" },
    { id: "users", label: "Active Users" },
    { id: "cars", label: "Popular Cars" },
    { id: "searches", label: "Popular Searches" },
    { id: "listings", label: "Popular Listings" },
    { id: "activity", label: "Recent Activity" },
  ];

  return (
    <div className={classes.analyticsContainer}>
      <div className={classes.analyticsHeader}>
        <h1>Analytics Dashboard</h1>
        <div className={classes.analyticsPeriod}>
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`${classes.periodBtn} ${
                period === opt.value ? classes.periodActive : ""
              }`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={classes.analyticsTabs}>
        {sections.map((s) => (
          <button
            key={s.id}
            className={`${classes.analyticsTab} ${
              activeSection === s.id ? classes.analyticsTabActive : ""
            }`}
            onClick={() => setActiveSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {loading && activeSection === "overview" ? (
        <div className={classes.analyticsLoading}>Loading analytics...</div>
      ) : (
        <>
          {activeSection === "overview" && dashboard && (
            <div className={classes.analyticsOverview}>
              <div className={classes.statsGrid}>
                <div className={classes.statCard}>
                  <div className={classes.statValue}>
                    {dashboard.totalUsers}
                  </div>
                  <div className={classes.statLabel}>Total Users</div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statValue}>
                    {dashboard.activeUserCount}
                  </div>
                  <div className={classes.statLabel}>
                    Active Users ({period}d)
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statValue}>
                    {dashboard.totalEvents}
                  </div>
                  <div className={classes.statLabel}>
                    Total Events ({period}d)
                  </div>
                </div>
                <div className={classes.statCard}>
                  <div className={classes.statValue}>
                    {dashboard.activeUserCount > 0
                      ? Math.round(
                          dashboard.totalEvents / dashboard.activeUserCount
                        )
                      : 0}
                  </div>
                  <div className={classes.statLabel}>Avg Events/User</div>
                </div>
              </div>

              <h2 className={classes.headingH2}>Event Breakdown</h2>
              <div className={classes.eventBreakdown}>
                {dashboard.eventBreakdown?.map((item) => (
                  <div key={item._id} className={classes.eventRow}>
                    <span className={classes.eventName}>
                      {EVENT_LABELS[item._id] || item._id}
                    </span>
                    <div className={classes.eventBarContainer}>
                      <div
                        className={classes.eventBar}
                        style={{
                          width: `${Math.min(
                            100,
                            (item.count /
                              Math.max(
                                ...dashboard.eventBreakdown.map(
                                  (e) => e.count
                                )
                              )) *
                              100
                          )}%`,
                        }}
                      />
                    </div>
                    <span className={classes.eventCount}>{item.count}</span>
                  </div>
                ))}
              </div>

              {dashboard.dailyActivity?.length > 0 && (
                <>
                  <h2 className={classes.headingH2}>Daily Activity</h2>
                  <div className={classes.dailyChart}>
                    {dashboard.dailyActivity.slice(-14).map((day) => {
                      const maxEvents = Math.max(
                        ...dashboard.dailyActivity.map((d) => d.events)
                      );
                      return (
                        <div key={day._id} className={classes.dailyBar}>
                          <div className={classes.barWrapper}>
                            <div
                              className={classes.bar}
                              style={{
                                height: `${Math.max(
                                  4,
                                  (day.events / maxEvents) * 100
                                )}%`,
                              }}
                              title={`${day.events} events, ${day.uniqueUsers} users`}
                            />
                          </div>
                          <span className={classes.barLabel}>
                            {new Date(day._id).toLocaleDateString("en-US", {
                              month: "short",
                              day: "numeric",
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === "users" && (
            <div>
              <h2 className={classes.headingH2}>
                Most Active Users (Last {period} Days)
              </h2>
              <div className={classes.desktopView}>
                <div className={classes.tableContainer}>
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeaderRow}>
                        <th className={classes.tableHead}>#</th>
                        <th className={classes.tableHead}>User</th>
                        <th className={classes.tableHead}>Email</th>
                        <th className={classes.tableHead}>Actions</th>
                        <th className={classes.tableHead}>Searches</th>
                        <th className={classes.tableHead}>Views</th>
                        <th className={classes.tableHead}>Bids</th>
                        <th className={classes.tableHead}>Favorites</th>
                        <th className={classes.tableHead}>Last Active</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeUsers.map((user, i) => (
                        <tr key={user._id} className={classes.tableRow}>
                          <td className={classes.tableData}>{i + 1}</td>
                          <td className={classes.tableData}>
                            {user.firstName} {user.lastName}
                          </td>
                          <td className={classes.tableData}>{user.email}</td>
                          <td className={classes.tableData}>
                            <strong>{user.totalActions}</strong>
                          </td>
                          <td className={classes.tableData}>
                            {user.searches}
                          </td>
                          <td className={classes.tableData}>
                            {user.carViews}
                          </td>
                          <td className={classes.tableData}>{user.bids}</td>
                          <td className={classes.tableData}>
                            {user.favorites}
                          </td>
                          <td className={classes.tableData}>
                            {timeAgo(user.lastActive)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.mobileView}>
                <div className={classes.mobileCards}>
                  {activeUsers.map((user, i) => (
                    <div key={user._id} className={classes.mobileCard}>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>#{i + 1}</span>
                        <span className={classes.cardValue}>
                          {user.firstName} {user.lastName}
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Email</span>
                        <span className={classes.cardValue}>{user.email}</span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Total Actions</span>
                        <span className={classes.cardValue}>
                          <strong>{user.totalActions}</strong>
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Breakdown</span>
                        <span className={classes.cardValue}>
                          {user.searches}S / {user.carViews}V / {user.bids}B /{" "}
                          {user.favorites}F
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Last Active</span>
                        <span className={classes.cardValue}>
                          {timeAgo(user.lastActive)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {activeUsers.length === 0 && (
                <p className={classes.analyticsEmpty}>
                  No user activity recorded yet for this period.
                </p>
              )}
            </div>
          )}

          {activeSection === "cars" && (
            <div>
              <h2 className={classes.headingH2}>
                Most Popular Cars (Last {period} Days)
              </h2>
              <div className={classes.desktopView}>
                <div className={classes.tableContainer}>
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeaderRow}>
                        <th className={classes.tableHead}>#</th>
                        <th className={classes.tableHead}>Car</th>
                        <th className={classes.tableHead}>Views</th>
                        <th className={classes.tableHead}>Unique Viewers</th>
                        <th className={classes.tableHead}>Favorites</th>
                        <th className={classes.tableHead}>Bids</th>
                        <th className={classes.tableHead}>Inspections</th>
                        <th className={classes.tableHead}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularCars.map((car, i) => (
                        <tr key={car._id} className={classes.tableRow}>
                          <td className={classes.tableData}>{i + 1}</td>
                          <td
                            className={`${classes.tableData} ${classes.clickableCell}`}
                            onClick={() =>
                              window.open(`/detail/${car._id}`, "_blank")
                            }
                          >
                            {car.carName || `ID: ${car._id}`}
                          </td>
                          <td className={classes.tableData}>
                            {car.totalViews}
                          </td>
                          <td className={classes.tableData}>
                            {car.uniqueViewers}
                          </td>
                          <td className={classes.tableData}>
                            {car.totalFavorites}
                          </td>
                          <td className={classes.tableData}>
                            {car.totalBids}
                          </td>
                          <td className={classes.tableData}>
                            {car.totalInspections}
                          </td>
                          <td className={classes.tableData}>
                            <strong>{car.totalInteractions}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.mobileView}>
                <div className={classes.mobileCards}>
                  {popularCars.map((car, i) => (
                    <div key={car._id} className={classes.mobileCard}>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>#{i + 1}</span>
                        <span
                          className={`${classes.cardValue} ${classes.clickableCell}`}
                          onClick={() =>
                            window.open(`/detail/${car._id}`, "_blank")
                          }
                        >
                          {car.carName || `ID: ${car._id}`}
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Views</span>
                        <span className={classes.cardValue}>
                          {car.totalViews} ({car.uniqueViewers} unique)
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Engagement</span>
                        <span className={classes.cardValue}>
                          {car.totalFavorites}F / {car.totalBids}B /{" "}
                          {car.totalInspections}I
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {popularCars.length === 0 && (
                <p className={classes.analyticsEmpty}>
                  No car interaction data yet for this period.
                </p>
              )}
            </div>
          )}

          {activeSection === "searches" && (
            <div>
              <h2 className={classes.headingH2}>
                Most Popular Searches (Last {period} Days)
              </h2>
              <div className={classes.desktopView}>
                <div className={classes.tableContainer}>
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeaderRow}>
                        <th className={classes.tableHead}>#</th>
                        <th className={classes.tableHead}>Search Term</th>
                        <th className={classes.tableHead}>Times Searched</th>
                        <th className={classes.tableHead}>Unique Users</th>
                        <th className={classes.tableHead}>Last Searched</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularSearches?.searches?.map((search, i) => (
                        <tr key={search.query} className={classes.tableRow}>
                          <td className={classes.tableData}>{i + 1}</td>
                          <td className={classes.tableData}>
                            <strong>{search.query}</strong>
                          </td>
                          <td className={classes.tableData}>{search.count}</td>
                          <td className={classes.tableData}>
                            {search.uniqueUsers}
                          </td>
                          <td className={classes.tableData}>
                            {formatDate(search.lastSearched)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.mobileView}>
                <div className={classes.mobileCards}>
                  {popularSearches?.searches?.map((search, i) => (
                    <div key={search.query} className={classes.mobileCard}>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>#{i + 1}</span>
                        <span className={classes.cardValue}>
                          <strong>{search.query}</strong>
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Searched</span>
                        <span className={classes.cardValue}>
                          {search.count} times by {search.uniqueUsers} users
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {popularSearches?.filters?.length > 0 && (
                <>
                  <h2
                    className={classes.headingH2}
                    style={{ marginTop: "24px" }}
                  >
                    Most Used Filters
                  </h2>
                  <div className={classes.eventBreakdown}>
                    {popularSearches.filters.map((filter) => (
                      <div key={filter._id} className={classes.eventRow}>
                        <span className={classes.eventName}>{filter._id}</span>
                        <div className={classes.eventBarContainer}>
                          <div
                            className={classes.eventBar}
                            style={{
                              width: `${Math.min(
                                100,
                                (filter.count /
                                  Math.max(
                                    ...popularSearches.filters.map(
                                      (f) => f.count
                                    )
                                  )) *
                                  100
                              )}%`,
                            }}
                          />
                        </div>
                        <span className={classes.eventCount}>
                          {filter.count}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}

              {(!popularSearches?.searches ||
                popularSearches.searches.length === 0) && (
                <p className={classes.analyticsEmpty}>
                  No search data recorded yet for this period.
                </p>
              )}
            </div>
          )}

          {activeSection === "listings" && (
            <div>
              <h2 className={classes.headingH2}>
                Most Viewed Listings (Last {period} Days)
              </h2>
              <div className={classes.desktopView}>
                <div className={classes.tableContainer}>
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeaderRow}>
                        <th className={classes.tableHead}>#</th>
                        <th className={classes.tableHead}>Car</th>
                        <th className={classes.tableHead}>Views</th>
                        <th className={classes.tableHead}>Unique Viewers</th>
                        <th className={classes.tableHead}>Last Viewed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {popularListings.map((listing, i) => (
                        <tr key={listing.carId} className={classes.tableRow}>
                          <td className={classes.tableData}>{i + 1}</td>
                          <td
                            className={`${classes.tableData} ${classes.clickableCell}`}
                            onClick={() =>
                              window.open(
                                `/detail/${listing.carId}`,
                                "_blank"
                              )
                            }
                          >
                            {listing.carName || `ID: ${listing.carId}`}
                          </td>
                          <td className={classes.tableData}>
                            {listing.views}
                          </td>
                          <td className={classes.tableData}>
                            {listing.uniqueViewers}
                          </td>
                          <td className={classes.tableData}>
                            {formatDate(listing.lastViewed)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.mobileView}>
                <div className={classes.mobileCards}>
                  {popularListings.map((listing, i) => (
                    <div key={listing.carId} className={classes.mobileCard}>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>#{i + 1}</span>
                        <span
                          className={`${classes.cardValue} ${classes.clickableCell}`}
                          onClick={() =>
                            window.open(
                              `/detail/${listing.carId}`,
                              "_blank"
                            )
                          }
                        >
                          {listing.carName || `ID: ${listing.carId}`}
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Views</span>
                        <span className={classes.cardValue}>
                          {listing.views} ({listing.uniqueViewers} unique)
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Last Viewed</span>
                        <span className={classes.cardValue}>
                          {formatDate(listing.lastViewed)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {popularListings.length === 0 && (
                <p className={classes.analyticsEmpty}>
                  No listing view data yet for this period.
                </p>
              )}
            </div>
          )}

          {activeSection === "activity" && (
            <div>
              <h2 className={classes.headingH2}>Recent Activity</h2>
              <div className={classes.desktopView}>
                <div className={classes.tableContainer}>
                  <table className={classes.table}>
                    <thead>
                      <tr className={classes.tableHeaderRow}>
                        <th className={classes.tableHead}>Time</th>
                        <th className={classes.tableHead}>User</th>
                        <th className={classes.tableHead}>Action</th>
                        <th className={classes.tableHead}>Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentActivity.map((event) => (
                        <tr key={event._id} className={classes.tableRow}>
                          <td className={classes.tableData}>
                            {formatDateTime(event.createdAt)}
                          </td>
                          <td className={classes.tableData}>
                            {event.firstName
                              ? `${event.firstName} ${event.lastName}`
                              : "Anonymous"}
                          </td>
                          <td className={classes.tableData}>
                            <span
                              className={`${classes.eventBadge} ${
                                classes[
                                  `badge_${event.eventType}`
                                ] || ""
                              }`}
                            >
                              {EVENT_LABELS[event.eventType] ||
                                event.eventType}
                            </span>
                          </td>
                          <td className={classes.tableData}>
                            {event.metadata?.searchQuery && (
                              <span>
                                Searched: &quot;{event.metadata.searchQuery}
                                &quot;
                              </span>
                            )}
                            {event.metadata?.carName && (
                              <span>{event.metadata.carName}</span>
                            )}
                            {event.metadata?.carId &&
                              !event.metadata?.carName && (
                                <span>Car: {event.metadata.carId}</span>
                              )}
                            {event.metadata?.bidAmount && (
                              <span>
                                {" "}
                                - Bid: ¥
                                {event.metadata.bidAmount.toLocaleString()}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={classes.mobileView}>
                <div className={classes.mobileCards}>
                  {recentActivity.map((event) => (
                    <div key={event._id} className={classes.mobileCard}>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Time</span>
                        <span className={classes.cardValue}>
                          {timeAgo(event.createdAt)}
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>User</span>
                        <span className={classes.cardValue}>
                          {event.firstName
                            ? `${event.firstName} ${event.lastName}`
                            : "Anonymous"}
                        </span>
                      </div>
                      <div className={classes.cardRow}>
                        <span className={classes.cardLabel}>Action</span>
                        <span className={classes.cardValue}>
                          {EVENT_LABELS[event.eventType] || event.eventType}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {recentActivity.length === 0 && (
                <p className={classes.analyticsEmpty}>
                  No activity recorded yet.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
